import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useForm } from '@tanstack/react-form'
import { cn } from '~/shared/common/utils'
import { Button } from '~/client/components/ui/button'
import { Card, CardContent } from '~/client/components/ui/card'
import { Input } from '~/client/components/ui/input'
import { Label } from '~/client/components/ui/label'
import { RevelStreetLogoHeaderIcon } from '~/client/components/icons'
import { useAuthStore } from '~/client/common/stores/auth'
import { capture, PostHogEvent } from '~/client/common/posthog'
import { requestActivation } from '~/client/activation/activation.api'
import { TurnstileWidget } from '~/client/activation/turnstile-widget'

export const Route = createFileRoute('/activate')({
  beforeLoad: async () => {
    await useAuthStore.getState().initializeAuth()
    if (useAuthStore.getState().user) {
      throw redirect({ href: '/' })
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const [submitted, setSubmitted] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined)
  const onToken = useCallback((token: string | undefined) => setTurnstileToken(token), [])

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      capture(PostHogEvent.ACTIVATION_REQUESTED)
      try {
        // The response is intentionally neutral and identical for every outcome.
        await requestActivation({ email: value.email, turnstileToken })
      } catch {
        // Swallow errors too — never reveal anything about the outcome.
      }
      setSubmitted(true)
    },
  })

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-sm">
        <div className={cn('flex flex-col gap-6')}>
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-1">
              <div className="p-6 md:p-8">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col items-center text-center">
                    <RevelStreetLogoHeaderIcon className="h-16 w-auto text-[#18003D]" />
                  </div>

                  {submitted ? (
                    <div className="flex flex-col gap-4 text-center">
                      <h1 className="text-2xl font-bold text-[#18003D]">Check your inbox.</h1>
                      <p className="text-muted-foreground text-balance">If this email is associated with an active subscription, you'll receive a setup link shortly.</p>
                      <Link to="/login" className="text-sm underline underline-offset-4">
                        Back to Login
                      </Link>
                    </div>
                  ) : (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        void form.handleSubmit()
                      }}
                    >
                      <div className="flex flex-col gap-6">
                        <div className="flex flex-col gap-2 text-center">
                          <h1 className="text-2xl font-bold text-[#18003D]">Activate your account</h1>
                          <p className="text-muted-foreground text-balance">Enter your work email and we'll send you a link to set up your account.</p>
                        </div>

                        <div className="grid gap-3">
                          <Label htmlFor="email">Email</Label>
                          <form.Field
                            name="email"
                            validators={{
                              onChange: ({ value }) => (!value ? 'Email is required' : /\S+@\S+\.\S+/.test(value) ? undefined : 'Enter a valid email'),
                            }}
                            children={(field) => (
                              <>
                                <Input
                                  id={field.name}
                                  type="email"
                                  placeholder="m@example.com"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(e) => field.handleChange(e.target.value)}
                                  aria-invalid={field.state.meta.errors.length ? 'true' : 'false'}
                                />
                                {field.state.meta.errors[0] ? <span className="text-sm text-destructive">{field.state.meta.errors[0]}</span> : null}
                              </>
                            )}
                          />
                        </div>

                        <TurnstileWidget onToken={onToken} />

                        <form.Subscribe
                          selector={(state) => [state.canSubmit, state.isSubmitting] as const}
                          children={([canSubmit, isSubmitting]) => (
                            <Button type="submit" className="w-full bg-[#8A3DFF] hover:bg-[#7B2FE6]" disabled={!canSubmit || isSubmitting}>
                              {isSubmitting ? 'Sending...' : 'Send me a link'}
                            </Button>
                          )}
                        />

                        <div className="text-center text-sm">
                          <Link to="/login" className="underline underline-offset-4">
                            Back to Login
                          </Link>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
